import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import resolveDashboardRole from '../dashboard/resolveDashboardRole';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // PIN session state — only used when logged into a station account.
  // The token is also kept in localStorage so api.js can inject the
  // X-Pin-Session header on every request.
  const [pinSession, setPinSession] = useState(() => {
    try {
      const raw = localStorage.getItem('pinSessionData');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (token) {
      api.getMe()
        .then(data => { setUser(data); setLoading(false); })
        .catch((err) => {
          // Only logout on auth-specific errors (401, token issues)
          // Server 500s should NOT kick the user to login
          const msg = (err.message || '').toLowerCase();
          if (msg.includes('authentication') || msg.includes('invalid') || msg.includes('expired') || msg.includes('unauthorized')) {
            logout();
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line
  }, [token]);

  function login(tokenValue, userData) {
    localStorage.setItem('token', tokenValue);
    setToken(tokenValue);
    setUser(userData);
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('pinSession');
    localStorage.removeItem('pinSessionData');
    setToken(null);
    setUser(null);
    setPinSession(null);
  }

  // PIN session helpers ─────────────────────────────────────────────
  // applyPinSession is called after a successful pin login response.
  // It stores the session token (for the X-Pin-Session header) and
  // the active dispatcher payload (for the StationHeader).
  function applyPinSession(payload) {
    localStorage.setItem('pinSession', payload.session_token);
    localStorage.setItem('pinSessionData', JSON.stringify(payload));
    setPinSession(payload);
  }
  function clearPinSession() {
    localStorage.removeItem('pinSession');
    localStorage.removeItem('pinSessionData');
    setPinSession(null);
  }

  // Permission check helper (global)
  function can(permission) {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  }

  // Module-level permission check
  function canInModule(moduleKey, permission) {
    if (!user) return false;
    const modPerms = user.modulePermissions?.[moduleKey];
    if (modPerms && modPerms[permission] !== undefined) return modPerms[permission] === true;
    return can(permission); // fallback to global
  }

  // Check if user can configure a module's setup
  function isConfigurator(moduleKey) {
    if (!user) return false;
    const t = user.permission_tier;
    if (t === 'super_admin') return true;
    return (user.configurator_modules || []).includes(moduleKey);
  }

  // Permission tier — only super_admin matters now (locked admin)
  const tier = user?.permission_tier || null;
  const isSuperAdmin = tier === 'super_admin' || user?.position_name === 'Super Admin';

  // Position-based identity
  const positionName = user?.position_name || null;
  const teamId = user?.team_id || null;
  const teamName = user?.team_name || null;
  const teamMemberships = user?.teamMemberships || [];

  // Manager helpers — based on team_members.is_manager
  const managedTeams = teamMemberships.filter(m => m.is_manager === 1);
  const isManager = managedTeams.length > 0 || positionName === 'Manager';
  const isAtLeastManager = isSuperAdmin || isManager;
  const isAtLeastAdmin = isSuperAdmin || tier === 'admin';
  const isSupervisor = isManager;

  function isManagerOf(teamNameToCheck) {
    return managedTeams.some(m => m.team_name === teamNameToCheck);
  }

  function isOnTeam(teamNameToCheck) {
    return teamMemberships.some(m => m.team_name === teamNameToCheck);
  }

  // Position-based identity checks
  // Day/Night Driver are shift variants of the Driver position — all three
  // must hit every driver gate (the dashboard resolver already treats them
  // as drivers; sidebar/hub gates must agree).
  const isDriver = positionName === 'Driver' || positionName === 'Day Driver' || positionName === 'Night Driver';
  const isDispatcher = positionName === 'Dispatcher';
  const isSales = positionName === 'Sales';
  const isSalesAdmin = positionName === 'Sales Admin';
  const isMaintenance = positionName === 'Truck Maintenance';
  const isSignTechnician = positionName === 'Sign Technician';
  // For backwards compatibility — fallback to team membership
  const effectiveTeam = teamName || null;

  // Effective team names (direct + ancestors via sub-team hierarchy).
  // Backend populates on login + /auth/me. Fall back to [team_name]
  // if the server didn't send it (stale JWT from before the migration).
  const effectiveTeamNames = Array.isArray(user?.effective_team_names)
    ? user.effective_team_names
    : (teamName ? [teamName] : []);

  // Server-derived convenience fields — SINGLE SOURCE OF TRUTH.
  // Prefer these over recomputing slug/role on the client. Any client
  // code that reaches for a hardcoded { 'Driver Operations': ... } map
  // should use these instead (see server/utils/teamHierarchy.js).
  const legacyRole = user?.legacy_role || user?.role || null;
  const teamSlug = user?.team_slug || null;

  // Sub-team slug for dashboard tiles (e.g. Dallas driver → 'dallas').
  // If the user's DIRECT team is a sub-team (has a parent in the chain),
  // slugify its name. Otherwise null so callers can pick the parent
  // default ('driver' for Driver Operations, etc.).
  //   'Dallas Drivers'  → 'dallas'
  //   'Day Drivers'     → 'day'
  //   'Fort Worth Drivers' → 'fort_worth'
  //   'Denton Drivers'  → 'denton'
  //   'Driver Operations' (direct — not a sub-team) → null
  const subTeamSlug = useMemo(() => {
    if (!teamName || effectiveTeamNames.length < 2) return null;
    // Two-or-more entries means team_name is a sub-team (own + ancestors).
    // Slugify matches server/utils/teamHierarchy.js slugifyTeamName.
    if (teamName === 'Driver Operations') return null;
    return String(teamName)
      .replace(/\s+(Drivers|Operations)\s*$/i, '')
      .toLowerCase()
      .replace(/\s+/g, '_');
  }, [teamName, effectiveTeamNames]);

  // Dashboard layout key (e.g. 'ceo', 'driver', 'sales'). Memoized so the
  // DashboardPage doesn't re-resolve on every render.
  const dashboardRole = useMemo(() => resolveDashboardRole(user), [user]);

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, loading, can, canInModule, isConfigurator,
      isSuperAdmin, isAtLeastAdmin, isAtLeastManager, isManager, isSupervisor,
      isManagerOf, isOnTeam, managedTeams, teamMemberships,
      teamId, teamName, positionName, effectiveTeam, effectiveTeamNames,
      subTeamSlug, legacyRole, teamSlug,
      isDriver, isDispatcher, isSales, isSalesAdmin, isMaintenance, isSignTechnician,
      dashboardRole,
      // PIN session layer
      pinSession, applyPinSession, clearPinSession,
      isStationAccount: !!user?.is_station_account,
      stationRegion: user?.station_region || null,
      // The "real" acting user — falls back to JWT user when no PIN active
      actingUserId: pinSession?.active_user?.id || user?.id || null,
      actingFullName: pinSession?.active_user?.full_name || user?.full_name || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
